import { Injectable, DestroyRef, signal, Signal, computed } from '@angular/core';
import { outputToObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { NgHttpCachingService } from 'ng-http-caching';
import { catchError, combineLatest, concatMap, map, Observable, of, take, tap } from 'rxjs';

import { OktaInterfaceService } from '@shure/cloud/shared/okta/data-access';
import {
	GetMyCompaniesResponse,
	OrganizationsApiService,
	StrictHttpResponse,
	CreateOrganizationsDto
} from '@shure/cloud/shared/organization-switcher/data-access';
import { OrganizationsStoreService } from '@shure/cloud/shared/services/organizations-store-service';
import { ApiUsersService } from '@shure/cloud/shared/users/data-access';
import { PrismDialogRef, PrismDialogService, PrismSnackbarService } from '@shure/prism-angular-components';
import {
	SwitchOrganizationDialogComponent,
	SwitchOrganizationDialogData
} from '@shure/shared/angular/features/switch-organization';
import { asLabel } from '@shure/shared/models';

import { CreateOrganizationDialogComponent } from '../ui/create-organization/create-organization-dialog.component';

@Injectable({
	providedIn: 'root'
})
export class OrganizationSwitcherService {
	public isSwitchOrgFromAddLicenseFlow = false;
	public disableReloadAfterOrgSwitch = false;
	public code: string | null = null;
	private reInitiateAddLicenses = signal(false);

	constructor(
		private router: Router,
		private activatedRoute: ActivatedRoute,
		private prismDialogService: PrismDialogService,
		private apiOrganizationsService: OrganizationsApiService,
		private snackBarService: PrismSnackbarService,
		private translocoService: TranslocoService,
		private readonly oktaIntfService: OktaInterfaceService,
		private readonly destroyRef: DestroyRef,
		private ngHttpCachingService: NgHttpCachingService,
		private apiUsersService: ApiUsersService,
		private orgStoreService: OrganizationsStoreService
	) {}

	/**
	 * Opens the Prism dialog for creating a new organization.
	 *
	 * @returns A reference to the opened CreateOrganizationDialogComponent instance.
	 */
	public openCreateOrgDialog(): PrismDialogRef<CreateOrganizationDialogComponent> {
		return this.prismDialogService.openDialog(CreateOrganizationDialogComponent);
	}

	/**
	 * Opens the Prism dialog for switching the organization.
	 * Optionally accepts a code to pass as dialog data.
	 *
	 * @param code - Optional string used to initialize dialog context.
	 * @returns A reference to the opened SwitchOrganizationDialogComponent instance.
	 */
	public openSwitchOrgDialog(
		code?: string,
		isSwitchOrgFromAddLicenseFlow?: boolean,
		dialogData?: SwitchOrganizationDialogData
	): PrismDialogRef<SwitchOrganizationDialogComponent> {
		this.code = code ?? null;
		this.isSwitchOrgFromAddLicenseFlow = !!isSwitchOrgFromAddLicenseFlow;
		this.disableReloadAfterOrgSwitch = dialogData?.disableReloadAfterOrgSwitch ?? false;
		const dialogRef = this.prismDialogService.openDialog(SwitchOrganizationDialogComponent, {
			data: { code: code, showCreateButton: true, ...dialogData },
			id: 'switch-org-dialog'
		});
		dialogRef.componentInstance.createRequested.subscribe(() => {
			dialogRef.close();
			this.openCreateOrgDialog();
		});
		outputToObservable(dialogRef.componentInstance.organizationSelected)
			.pipe(take(1))
			.subscribe((orgId) => {
				this.switchOrganization(orgId);
			});
		return dialogRef;
	}

	/**
	 * Switches the current organization session to the specified organization ID.
	 *
	 * This function sets the Okta session tenant to the given organization ID and reloads the window.
	 * The session is set through a service call to `setOktaSessionTenant$`.
	 *
	 * @param {string} organizationId - The ID of the organization to switch to.
	 */
	public switchOrganization(organizationId: string): void {
		if (this.disableReloadAfterOrgSwitch) {
			this.oktaIntfService.disableReloadAfterOrgSwitch = true;
		}

		// IMPORTANT: Do not remove this navigation.
		// This ensures the 'code' query parameter is present in the URL before the page reloads (which passes a claim code via the url)
		// This is for retaining the redeem claim code and redeem/add claim code dialog should be open during an organization switch.
		if (this.code !== null && this.code !== undefined) {
			this.router.navigate([], {
				queryParams: {
					code: this.code
				},
				queryParamsHandling: 'merge',
				replaceUrl: true
			});
		}
		this.oktaIntfService
			.setOktaSessionTenant$(organizationId, true)
			.pipe(
				map(() => true),
				catchError(() => {
					return of(false);
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe({
				next: (result: boolean) => {
					if (!result) {
						// if we can't change the tenant, not much we can do. I'm reloading the
						// page to make sure we're still logged in.
						this.reloadWindow();
					} else {
						this.ngHttpCachingService.clearCacheByRegex(/\/permissions/);
						// If the orgId query parameter is in the URL, reload the page with the new orgId value.
						// Otherwise, we'll reload the page with the parameter removed, merging other parameters.
						const existingOrgIdQueryParameter = this.activatedRoute.snapshot.queryParamMap.get('orgId');
						this.router
							.navigate([], {
								queryParams: {
									orgId: existingOrgIdQueryParameter !== null ? organizationId : null
								},
								queryParamsHandling: 'merge'
							})
							.then(() => {
								if (this.disableReloadAfterOrgSwitch) {
									return;
								} else if (!this.isSwitchOrgFromAddLicenseFlow) {
									this.reloadWindow();
								} else {
									this.handleOrgIdQueryParameter().pipe(take(1)).subscribe();
									this.isSwitchOrgFromAddLicenseFlow = false;
								}
							});
					}
				}
			});
	}

	/**
	 * Sends a request to create a new organization and switch to the new organization.
	 *
	 * This method takes organization data (`createOrgData`) and makes an API call to create a verified organization.
	 * If the organization is created successfully, a message is displayed via a Snackbar. If the creation fails,
	 * an error message is displayed.
	 *
	 * @param {CreateOrganizationsDto} createOrgData - The data needed to create the organization.
	 */
	public createOrganization(createOrgData: CreateOrganizationsDto): void {
		this.apiOrganizationsService.createVerifiedOrganization$Response({ body: createOrgData }).subscribe({
			next: (createResponse) => {
				const orgId = createResponse.body?.body?.data?.id;
				if (orgId) {
					this.switchOrganization(orgId);
				}
				this.snackBarService.open({
					message: this.translocoService.translate('cloud.shared.switch-org.organization-created'),
					actionText: this.translocoService.translate('cloud.shared.button-text.ok')
				});
			},
			error: (error) => {
				const errorMessage = error.error;
				this.snackBarService.open({
					message: this.translocoService.translate(
						errorMessage?.i18nKey
							? 'cloud.shared.error-labels.' + errorMessage.i18nKey
							: errorMessage.message
					),
					actionText: this.translocoService.translate('common.snackbar.ok')
				});
			}
		});
	}

	/**
	 * Retrieves the unique list of companies associated with the current user.
	 * This method makes an API call to get a list of companies that the current user is associated with.
	 */
	public getMyCompanies(): Observable<StrictHttpResponse<GetMyCompaniesResponse>> {
		return this.apiOrganizationsService.getMyUniqueCompanies$Response();
	}

	public getAllMyOrganizationIds$(): Observable<string[]> {
		return this.apiOrganizationsService.getAllMyOrganizations$Response().pipe(
			map((orgData) => {
				const items = orgData.body.body?.items;
				if (!items) {
					return [];
				}
				return items.map((item) => item.id ?? '');
			}),
			catchError(() => {
				this.displaySnackbarError(asLabel('cloud.shared.switch-org.loading-orgs-failure'));
				return of([]);
			})
		);
	}

	/**
	 * Get re initiate add licenses flow
	 * @returns reInitiateAddLicenses - boolean representation of re initiate add licenses flow
	 */
	public getReInitiateAddLicenses(): Signal<boolean> {
		return computed(() => this.reInitiateAddLicenses());
	}

	/**
	 * Set re initiate add licenses flow
	 *  * @param reInitiate - boolean representation of re initiate add licenses flow
	 */
	public setReInitiateAddLicenses(reInitiate: boolean): void {
		this.reInitiateAddLicenses.set(reInitiate);
	}

	// checks for the existence of the OrgId query parameter and if present
	// will see if the user's org needs to be switched.
	// returns a boolean observer so callers know when the work is done

	public handleOrgIdQueryParameter(): Observable<boolean> {
		const requestedOrgId$ = this.activatedRoute.queryParamMap.pipe(
			map((queryParamMap) => queryParamMap.get('orgId')),
			map((orgId) => orgId?.replace(/["']/g, '') ?? '') // remove leading/training quotes
		);
		const currentOrgId$ = this.oktaIntfService.getUserProfile$().pipe(map((userProfile) => userProfile.orgId));
		const allMyOrgIds$ = this.getAllMyOrganizationIds$();

		return combineLatest([requestedOrgId$, currentOrgId$, allMyOrgIds$]).pipe(
			concatMap(([requestedOrgId, currentOrgId, allOrgIds]) => {
				return this.checkAndSwitchOrgs(requestedOrgId, currentOrgId, allOrgIds);
			}),
			takeUntilDestroyed(this.destroyRef)
		);
	}

	private reloadWindow(): void {
		window.location.reload();
	}

	private checkAndSwitchOrgs(requestedOrgId: string, currentOrgId: string, allOrgIds: string[]): Observable<boolean> {
		// no requested org, can't switch
		if (!requestedOrgId || requestedOrgId.length === 0) {
			return of(true);
		}

		// already on this org... no switching is necessary.
		if (requestedOrgId === currentOrgId) {
			return of(true);
		}

		// if the requested Org Id is not a valid org for the user put up a snackbar,
		// then remove the orgId parameter from the URL by routing to '/' w/out the query parameter
		if (!allOrgIds.includes(requestedOrgId)) {
			this.displaySnackbarError(asLabel('cloud.shared.switch-org.not-a-member-of-org'));
			// Normally, we don't want to muck with the query parameters, but since it is not valid
			// route to / to clean up the URL.
			this.router.navigate(['/']).then();
			return of(true);
		}

		const result = this.oktaIntfService.setOktaSessionTenant$(requestedOrgId, false).pipe(
			catchError(() => {
				this.displaySnackbarError(asLabel('cloud.shared.switch-org.okta-tenant-update-failure'));
				return of(true);
			}),
			concatMap(() => {
				// Instead of refreshing the page, reload the account info.  This will trigger the update
				// of account/user info throughout the app, w/out the page reload.
				return this.reloadAccountInfo();
			})
		);

		return result;
	}

	private reloadAccountInfo(): Observable<boolean> {
		return this.apiUsersService.getSessionAccountInfo$Response().pipe(
			tap((accountInfoResp) => {
				if (accountInfoResp?.body?.body) {
					this.orgStoreService.setAccountInfo(accountInfoResp.body.body);
				}
			}),
			map(() => true),
			catchError(() => {
				this.displaySnackbarError(asLabel('cloud.shared.switch-org.reload-account-failure'));
				return of(true);
			})
		);
	}

	private displaySnackbarError(key: string): void {
		this.snackBarService.open({
			message: this.translocoService.translate(key),
			actionText: this.translocoService.translate('common.snackbar.dismiss')
		});
	}
}
